import { inject, Injectable, Renderer2 } from '@angular/core';
import { IPayout, ManageableStorageLocation } from '@luggagehero/shared/interfaces';

import { InvoiceServiceStrategy } from './invoice-service.strategy';
import { SharedInvoiceV1Service } from './shared-invoice-v1.service';
import { SharedInvoiceV2Service } from './shared-invoice-v2.service';
import { SharedInvoiceV2AlternateService } from './shared-invoice-v2-alternate.service';

/**
 * TEMPORARY: v2 payouts are currently routed to the alternate implementation
 * (`SharedInvoiceV2AlternateService`) while we iterate on a redesigned layout.
 * Once the design is locked in, the alternate's contents will replace the
 * original v2 and the alternate service + this flag will be removed.
 */
const USE_V2_ALTERNATE = false;

@Injectable({
  providedIn: 'root',
})
export class SharedInvoiceService implements InvoiceServiceStrategy {
  private v1 = inject(SharedInvoiceV1Service);
  private v2 = inject(SharedInvoiceV2Service);
  private v2Alternate = inject(SharedInvoiceV2AlternateService);

  public get isReady(): boolean {
    return this.v1.isReady && this.v2.isReady && this.v2Alternate.isReady;
  }

  public init(renderer: Renderer2, doc: unknown): void {
    // Init all implementations — at init time we don't yet know which payout
    // versions the user will open during the session.
    this.v1.init(renderer, doc);
    this.v2.init(renderer, doc);
    this.v2Alternate.init(renderer, doc);
  }

  public open(payout: IPayout, shop: ManageableStorageLocation): void {
    this.pick(payout).open(payout, shop);
  }

  public print(payout: IPayout, shop: ManageableStorageLocation): void {
    this.pick(payout).print(payout, shop);
  }

  public download(payout: IPayout, shop: ManageableStorageLocation): void {
    this.pick(payout).download(payout, shop);
  }

  public getDocDefinition(payout: IPayout, shop: ManageableStorageLocation) {
    return this.pick(payout).getDocDefinition(payout, shop);
  }

  private pick(payout: IPayout): InvoiceServiceStrategy {
    if (payout.version === 'v2') {
      return USE_V2_ALTERNATE ? this.v2Alternate : this.v2;
    }
    return this.v1;
  }
}
